import { handleError } from '../utils/errorHandler';
import { getAuthHeadersFromStorage } from '../utils/httpUtils';
import type {
  LanguageDetectionRequest,
  LanguageDetectionResponse,
  TranslationRequest,
  TranslationResponse,
} from '../types';

/**
 * Standardized API response wrapper format
 */
interface ApiResponseWrapper<T> {
  success: boolean;
  data: T;
  error?: string;
}

/**
 * Unwrap standardized API response format {success, data, error}.
 * Throws error if API returned success: false.
 */
function unwrapApiResponse<T>(json: ApiResponseWrapper<T>): T {
  if (!json.success && json.error) {
    throw new Error(json.error);
  }
  return json.data;
}

/**
 * Translation API Service
 * Handles text translation operations using NLLB-200 with optional AI boost
 */
export class TranslationAPI {
  /**
   * Get the base URL for the API
   */
  private static async getBaseUrl(): Promise<string> {
    // Reuse existing API base URL logic from VideoGenerationAPI
    const { VideoGenerationAPI } = await import('./api');
    return VideoGenerationAPI.getBaseUrl();
  }

  /**
   * Translate text to one or more target languages
   * @param request - Translation request with text and language codes
   * @returns Translation response with results for each target language
   */
  static async translateText(request: TranslationRequest): Promise<TranslationResponse> {
    try {
      console.log('[TranslationAPI] Getting base URL...');
      const baseUrl = await this.getBaseUrl();
      console.log('[TranslationAPI] Base URL:', baseUrl);
      console.log('[TranslationAPI] Sending request to:', `${baseUrl}/api/v1/translate`);

      const response = await fetch(`${baseUrl}/api/v1/translate`, {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify(request),
      });

      console.log('[TranslationAPI] Response status:', response.status);
      const rawResponse = await response.json();
      console.log('[TranslationAPI] Response data:', rawResponse);

      if (!response.ok) {
        const errorMsg =
          rawResponse.error?.message || rawResponse.error || 'Translation request failed';
        throw new Error(errorMsg);
      }

      // Unwrap standardized API format {success, data, error}
      const translationData = unwrapApiResponse<{
        source_language: string;
        source_text: string;
        translations: TranslationResponse['translations'];
        processing_time_seconds?: number;
        detected_language?: string;
        detection_confidence?: number;
      }>(rawResponse);

      return {
        success: true,
        source_language: translationData.source_language,
        source_text: translationData.source_text,
        translations: translationData.translations || [],
        processing_time_seconds: translationData.processing_time_seconds,
        detected_language: translationData.detected_language,
        detection_confidence: translationData.detection_confidence,
      };
    } catch (error: unknown) {
      console.error('[TranslationAPI] Error:', error);
      const errorMessage = handleError(error, 'Translation', 'Failed to translate text');
      return {
        success: false,
        source_language: request.source_language,
        source_text: request.text,
        translations: [],
        error: errorMessage,
      };
    }
  }

  /**
   * Get list of supported languages for translation
   * @returns List of supported language codes
   */
  static async getSupportedLanguages(): Promise<{
    success: boolean;
    languages: string[];
    total: number;
    error?: string;
  }> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/translate/languages`, {
        headers: await getAuthHeadersFromStorage(),
      });
      const rawData = await response.json();

      if (!response.ok) {
        throw new Error(rawData.error || 'Failed to get supported languages');
      }

      // Unwrap standardized API format {success, data, error}
      const data = unwrapApiResponse<{ languages: string[]; total: number }>(rawData);
      return {
        success: true,
        languages: data.languages || [],
        total: data.total || 0,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Translation', 'Failed to get supported languages');
      return {
        success: false,
        languages: [],
        total: 0,
        error: errorMessage,
      };
    }
  }

  /**
   * Detect language from text
   * @param request - Language detection request with text
   * @returns Detected language info with confidence score
   */
  static async detectLanguage(
    request: LanguageDetectionRequest
  ): Promise<LanguageDetectionResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/translate/detect`, {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify(request),
      });

      const rawResponse = await response.json();

      if (!response.ok) {
        throw new Error(rawResponse.error || 'Language detection failed');
      }

      // Unwrap standardized API format {success, data, error}
      const detectionData = unwrapApiResponse<{
        detected_language: string;
        language_name: string;
        confidence: number;
        is_nllb_supported: boolean;
        nllb_code?: string;
      }>(rawResponse);

      return {
        success: true,
        detected_language: detectionData.detected_language,
        language_name: detectionData.language_name,
        confidence: detectionData.confidence,
        is_nllb_supported: detectionData.is_nllb_supported,
        nllb_code: detectionData.nllb_code,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Translation', 'Failed to detect language');
      return {
        success: false,
        detected_language: 'unknown',
        language_name: 'Unknown',
        confidence: 0,
        is_nllb_supported: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Get list of languages that can be auto-detected
   * @returns List of detectable language codes
   */
  static async getDetectableLanguages(): Promise<{
    success: boolean;
    languages: string[];
    total: number;
    error?: string;
  }> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/translate/detect/languages`, {
        headers: await getAuthHeadersFromStorage(),
      });
      const rawData = await response.json();

      if (!response.ok) {
        throw new Error(rawData.error || 'Failed to get detectable languages');
      }

      // Unwrap standardized API format {success, data, error}
      const data = unwrapApiResponse<{ languages: string[]; total: number }>(rawData);
      return {
        success: true,
        languages: data.languages || [],
        total: data.total || 0,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Translation', 'Failed to get detectable languages');
      return {
        success: false,
        languages: [],
        total: 0,
        error: errorMessage,
      };
    }
  }
}
